export type FormValue = string | boolean | number | null | FormValue[] | { [x: string]: FormValue };
export type FormValues = Record<string, FormValue>;
export type FormError = React.ReactNode;
/**
 * This adheres to the redux-form interface. More arguments are actually
 * provided, but rules generally do not need the other 2 arguments (field props
 * and field name).
 *
 * @returns {FormError} value to display as form error, or nothing if the rule passed
 */
export type Rule<Value = FormValue, AllValues = FormValues | undefined> = (
  value: Value,
  allValues?: AllValues,
) => FormError | undefined | null;

/**
 * Checks contain validation logic and have no message tied to them. Checks
 * should return `true` if valid.
 */
export type Check<Value = FormValue, AllValues = FormValues | undefined> = (
  value: Value,
  allValues?: AllValues,
) => boolean;
export type CheckCreator<Value = FormValue, AllValues = FormValues | undefined> = (
  ...args: any[]
) => Check<Value, AllValues>;

/**
 * Represents an object where the keys are form field names and the values are
 * rules.
 */
export type FormValidator<FormValues extends Record<string, any>> = Partial<
  Record<keyof FormValues, Rule | Rule[]>
>;

/**
 * Creates a Rule by appending an error message onto a Check.
 */
export const withMessage =
  <Value = FormValue, AllValues = FormValues | undefined>(
    message: string,
    check: Check<Value, AllValues>,
  ): Rule<Value, AllValues> =>
  (value, allValues) =>
    check(value, allValues) ? null : message;

/**
 * Creates a Rule by appending an error html message onto a Check.
 */
export const withComponent =
  <Value = FormValue, AllValues = FormValues | undefined>(
    component: JSX.Element,
    rule: Check<Value, AllValues>,
  ): Rule<Value, AllValues> =>
  (value, allValues) =>
    rule(value, allValues) ? null : component;

/**
 * Takes a check and returns a new check that sanitizes input. Useful for masks.
 */
export const sanitize =
  (regexp: RegExp, check: Check): Check<string> =>
  (value: string, allValues?: FormValues) => {
    const sanitizedValue = value?.replace(regexp, '');
    return check(sanitizedValue, allValues);
  };

/**
 * Applies a rule to all elements in an array value.
 */
export const every =
  (rule: Check): Check<FormValue[]> =>
  (value: FormValue[] = [], allValues?: FormValues) =>
    value.every((v) => rule(v, allValues));
